import { Eye, EyeOff, Languages } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/form'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { canUseDashboard } from '@/lib/permissions'
import { errorText } from '@/lib/utils'

export function LoginPage() {
  const { user, login, logout } = useAuth()
  const { t, lang, setLang } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (user && canUseDashboard(user)) {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? (user.role === 'superadmin' ? '/admin' : '/')} replace />
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const u = await login(identifier, password)
      if (!canUseDashboard(u)) {
        await logout()
        setError(t('This dashboard is for charge point operators and their staff. Drivers can use the Saadhan Charge app.'))
        return
      }
      navigate(u.role === 'superadmin' ? '/admin' : '/', { replace: true })
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1.1fr]">
      <div className="flex flex-col px-6 py-8 sm:px-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid size-9 place-items-center rounded-lg bg-ink">
              <svg viewBox="0 0 24 24" className="size-5 fill-brand" aria-hidden="true">
                <path d="M13.6 2 5 13.4h6.1L9.8 22l9.2-12.6h-6.3z" />
              </svg>
            </span>
            <span className="text-base font-semibold">
              Saadhan <span className="text-brand-ink dark:text-brand">Charge</span>
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLang(lang === 'en' ? 'ne' : 'en')}>
            <Languages /> {lang === 'en' ? 'नेपाली' : 'English'}
          </Button>
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-12">
          <h1 className="text-2xl font-semibold tracking-tight">{t('Sign in to your network')}</h1>
          <p className="mt-2 text-sm text-text-2">{t('For charge point operators, station staff and the Saadhan Charge team.')}</p>
          <form onSubmit={submit} className="mt-8 grid gap-4" noValidate>
            <Field label={t('Phone or email')}>
              {(p) => <Input {...p} value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required autoFocus />}
            </Field>
            <Field label={t('Password')}>
              {(p) => (
                <div className="relative">
                  <Input {...p} type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="pr-10" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-text" aria-label={show ? t('Hide password') : t('Show password')}>
                    {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              )}
            </Field>
            {error && (
              <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" size="lg" loading={busy} disabled={!identifier || !password}>
              {t('Sign in')}
            </Button>
          </form>
          <p className="mt-6 text-xs text-text-2">
            {t('Forgot your password? Ask your operator admin, or write to')}{' '}
            <a className="underline" href="mailto:summit@saadhan.app">
              summit@saadhan.app
            </a>
          </p>
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-ink text-white lg:block">
        <div className="absolute -right-40 -top-40 size-[640px] rounded-full bg-[radial-gradient(circle,rgba(255,180,0,0.28),transparent_62%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.04)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_70%_40%,#000_20%,transparent_70%)]" />
        <div className="relative flex h-full flex-col justify-end p-14">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand">{t('Charging station management')}</p>
          <h2 className="mt-3 max-w-md text-4xl font-semibold leading-tight tracking-tight">{t('Every charger, session and rupee, live.')}</h2>
          <p className="mt-4 max-w-md text-white/70">{t('Stations, chargers, pricing, customers, cards, payouts and NEA bills, on any OCPP 1.6J charger.')}</p>
        </div>
      </div>
    </div>
  )
}
